import React, { type FC, type ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { AlertBannerProvider } from "@kivunova/kivufrontendcommon";
import { Provider } from "react-redux";
import { store } from "./store/store";
import adminTheme from "./theme/adminTheme";
import { AdminAuthProvider } from "./context/AdminAuthContext";

/**
 * AppProviders for KivuIms Admin Portal UI.
 * Includes theme (white + blue), admin auth context, and optional router for standalone.
 *
 * @param wrapWithRouter - If true, wraps children with BrowserRouter (for standalone development).
 */

interface AppProvidersProps {
  children: ReactNode;
  wrapWithRouter?: boolean;
}

const AppProviders: FC<AppProvidersProps> = ({
  children,
  wrapWithRouter = false,
}) => {
  const queryClient = new QueryClient();

  const content = (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={adminTheme}>
        <CssBaseline />
        <AlertBannerProvider>
          <AdminAuthProvider>{children}</AdminAuthProvider>
        </AlertBannerProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );

  return (
    <React.StrictMode>
      <Provider store={store}>
        {wrapWithRouter ? <BrowserRouter>{content}</BrowserRouter> : content}
      </Provider>
    </React.StrictMode>
  );
};

export default AppProviders;
